import { useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Drawer,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk, useCreatorList } from '@/api/queries';
import type { AdminCreator, ImageRef } from '@/api/types';
import { Artwork } from '@/components/Artwork';
import { EmptyState } from '@/components/EmptyState';
import { ListSearch } from '@/components/ListSearch';
import { ListFooter } from '@/components/PagedList';
import { ImageSlot } from '@/components/ImageSlot';
import { PageHeader } from '@/components/PageHeader';
import { ErrorAlert, PageLoader } from '@/components/QueryState';
import { fromNow } from '@/lib/format';
import { useListUrlState } from '@/lib/useListUrlState';

export function CreatorsPage() {
  const url = useListUrlState();
  const showArchived = url.get('archived') === 'true';
  const list = useCreatorList({ q: url.q, archived: showArchived || undefined });
  const [editing, setEditing] = useState<AdminCreator | 'new' | null>(null);

  return (
    <>
      <PageHeader
        title="Creators"
        description="Creators and content partners. Every programme is attributed to one."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
            New creator
          </Button>
        }
      />
      <Group mb="md" gap="md">
        <ListSearch
          label="Search creators"
          placeholder="Search names"
          value={url.q}
          onSearch={url.setQ}
        />
        <Switch
          label="Show archived"
          checked={showArchived}
          onChange={(e) => url.setFilter('archived', e.currentTarget.checked ? 'true' : null)}
        />
      </Group>
      {list.isPending ? (
        <PageLoader />
      ) : list.error ? (
        <ErrorAlert error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          title={url.q ? 'No creators match' : 'No creators'}
          action={
            <Button variant="default" onClick={() => setEditing('new')}>
              New creator
            </Button>
          }
        >
          Add the studios and partners whose programmes you publish.
        </EmptyState>
      ) : (
        <>
          <Table.ScrollContainer minWidth={560}>
            <Table striped style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={56}>Logo</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Artwork image={c.logo} title={c.name} width={40} ratio={1} radius={8} />
                    </Table.Td>
                    <Table.Td>
                      <Anchor
                        component="button"
                        type="button"
                        fw={600}
                        c="inherit"
                        onClick={() => setEditing(c)}
                      >
                        {c.name}
                      </Anchor>
                      <div>
                        <Anchor component={Link} to={`/programmes?creator=${c.id}`} size="xs">
                          Programmes
                        </Anchor>
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {c.slug}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {c.archived ? (
                        <Badge color="gray">Archived</Badge>
                      ) : (
                        <Badge color="leaf">Active</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{fromNow(c.updated_at)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <ListFooter query={list} noun="creators" />
        </>
      )}
      <Drawer
        opened={!!editing}
        onClose={() => setEditing(null)}
        position="right"
        title={editing === 'new' ? 'New creator' : 'Edit creator'}
        size="md"
      >
        {editing && (
          <CreatorForm
            creator={editing === 'new' ? null : editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Drawer>
    </>
  );
}

function CreatorForm({ creator, onDone }: { creator: AdminCreator | null; onDone: () => void }) {
  const qc = useQueryClient();
  const form = useForm({
    initialValues: {
      name: creator?.name ?? '',
      slug: creator?.slug ?? '',
      logo: (creator?.logo ?? null) as ImageRef | null,
    },
    validate: { name: (v) => (v.trim() ? null : 'Add a name.') },
  });
  const done = (message: string) => {
    void qc.invalidateQueries({ queryKey: ['creators'] });
    if (creator) {
      void qc.invalidateQueries({ queryKey: qk.creator(creator.id) });
      // Programme lists show the creator's name.
      void qc.invalidateQueries({ queryKey: ['programmes'] });
    }
    notifications.show({ color: 'leaf', message });
    onDone();
  };
  const save = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = { name: v.name.trim(), slug: v.slug.trim() || undefined, logo: v.logo };
      return creator ? adminApi.updateCreator(creator.id, body) : adminApi.createCreator(body);
    },
    onSuccess: () => done(creator ? 'Creator saved.' : 'Creator added.'),
  });
  const toggle = useMutation({
    mutationFn: () =>
      creator!.archived
        ? adminApi.restoreCreator(creator!.id)
        : adminApi.archiveCreator(creator!.id),
    onSuccess: (c) => done(c.archived ? 'Creator archived.' : 'Creator restored.'),
    onError: (e) => notifications.show({ color: 'coral', message: errorMessage(e) }),
  });
  return (
    <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
      <Stack>
        <TextInput label="Name" required data-autofocus {...form.getInputProps('name')} />
        <TextInput
          label="Slug"
          description="Leave empty to generate from the name."
          {...form.getInputProps('slug')}
        />
        <ImageSlot
          kind="logo"
          label="Logo (optional)"
          hint="Square PNG or WebP, transparent background, at least 400 × 400 px."
          ratio={1}
          previewWidth={160}
          value={form.values.logo}
          onChange={(v) => form.setFieldValue('logo', v)}
        />
        {save.error && (
          <Text size="sm" c="coral.8" role="alert">
            {errorMessage(save.error)}
          </Text>
        )}
        <Group justify="space-between">
          {creator ? (
            <Button
              variant="subtle"
              color={creator.archived ? 'leaf' : 'coral'}
              loading={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {creator.archived ? 'Restore' : 'Archive'}
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="default" onClick={onDone}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
}
